<script lang="ts">
    import * as Popover from "./ui/popover/index.js";
    import * as Command from "./ui/command/index.js";
    import * as Avatar from "./ui/avatar/index.js";
    import * as Tooltip from "./ui/tooltip/index.js";
    import UserRoundPlus from "@lucide/svelte/icons/user-round-plus";
    import Check from "@lucide/svelte/icons/check";
    import { m } from "../messages.svelte.js";
    import { cn, getClient } from "../utils.js";
    import type { ActiveOrganization } from "../auth.svelte.js";

    type Member = ActiveOrganization["members"][number];

    interface Props {
        caseId: string;
        members: Member[];
        selectedUserIds: string[];
        onChanged?: () => void;
        compact?: boolean;
        class?: string;
    }

    const { caseId, members, selectedUserIds, onChanged, compact = false, class: className }: Props = $props();

    let localUserIds = $state<string[]>(selectedUserIds);

    $effect(() => {
        localUserIds = selectedUserIds;
    });

    let open = $state(false);

    const sortedMembers = $derived([...members].sort((a, b) => displayName(a).localeCompare(displayName(b))));
    const selectedMembers = $derived(sortedMembers.filter(mb => localUserIds.includes(mb.userId)));

    async function toggleOwner(userId: string) {
        const next = localUserIds.includes(userId)
            ? localUserIds.filter(id => id !== userId)
            : [...localUserIds, userId];
        localUserIds = next;
        await getClient()["Set case owners"]({ jsonBody: { caseId, userIds: next } });
        onChanged?.();
    }

    function initials(member: Member): string {
        const name = member.user.name;
        if (!name) return member.user.email?.charAt(0).toUpperCase() ?? "?";
        return name
            .split(" ")
            .map(p => p.charAt(0))
            .join("")
            .toUpperCase()
            .slice(0, 2);
    }

    function displayName(member: Member): string {
        return member.user.name || member.user.email;
    }
</script>

<div class={cn("flex items-center", className)} onclick={e => e.stopPropagation()}>
    <Popover.Root bind:open>
        <Popover.Trigger
            class={cn(
                "flex items-center rounded-md transition-colors hover:bg-accent",
                compact ? "gap-0.5 p-0.5" : "gap-1.5 px-1.5 py-1",
            )}
        >
            {#if selectedMembers.length > 0}
                <div class={cn("flex", compact ? "gap-0.5" : "-space-x-1")}>
                    {#each selectedMembers.slice(0, 3) as member (member.userId)}
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <Avatar.Root class={cn("rounded-[3px]", compact ? "size-5" : "size-7 border-2 border-background")}>
                                    {#if member.user.image}
                                        <Avatar.Image src={member.user.image} alt={displayName(member)} />
                                    {/if}
                                    <Avatar.Fallback class={cn("rounded-[3px] bg-primary font-medium text-primary-foreground", compact ? "text-[7px]" : "text-[9px]")}>
                                        {initials(member)}
                                    </Avatar.Fallback>
                                </Avatar.Root>
                            </Tooltip.Trigger>
                            <Tooltip.Content>{displayName(member)}</Tooltip.Content>
                        </Tooltip.Root>
                    {/each}
                    {#if selectedMembers.length > 3}
                        <Avatar.Root class={cn("rounded-[3px]", compact ? "size-5" : "size-7 border-2 border-background")}>
                            <Avatar.Fallback class={cn("rounded-[3px] bg-muted-foreground font-medium text-white", compact ? "text-[7px]" : "text-[9px]")}>
                                +{selectedMembers.length - 3}
                            </Avatar.Fallback>
                        </Avatar.Root>
                    {/if}
                </div>
            {:else}
                <UserRoundPlus class="text-muted-foreground size-3.5" />
                {#if !compact}
                    <span class="text-muted-foreground text-xs">{m["workbench.noOwner"]()}</span>
                {/if}
            {/if}
        </Popover.Trigger>
        <Popover.Content class="w-60 p-0" align="start">
            <Command.Root>
                <Command.Input placeholder={m["workbench.searchMembers"]()} />
                <Command.List>
                    <Command.Empty>{m["workbench.searchMembers"]()}</Command.Empty>
                    <Command.Group>
                        {#each sortedMembers as member (member.userId)}
                            {@const selected = localUserIds.includes(member.userId)}
                            <Command.Item
                                value={member.userId}
                                keywords={[member.user.name ?? "", member.user.email]}
                                onSelect={() => toggleOwner(member.userId)}
                            >
                                <div
                                    class={cn(
                                        "border-primary mr-1 flex h-4 w-4 items-center justify-center rounded-sm border",
                                        selected ? "bg-primary text-primary-foreground" : "opacity-50",
                                    )}
                                >
                                    {#if selected}
                                        <Check class="size-3" />
                                    {/if}
                                </div>
                                <Avatar.Root class="size-5 rounded-[3px]">
                                    {#if member.user.image}
                                        <Avatar.Image src={member.user.image} alt={displayName(member)} />
                                    {/if}
                                    <Avatar.Fallback class="rounded-[3px] bg-primary text-[8px] text-primary-foreground">
                                        {initials(member)}
                                    </Avatar.Fallback>
                                </Avatar.Root>
                                <span class="truncate">{displayName(member)}</span>
                            </Command.Item>
                        {/each}
                    </Command.Group>
                </Command.List>
            </Command.Root>
        </Popover.Content>
    </Popover.Root>
</div>
